import {Component, EventEmitter, Input, model, OnDestroy, OnInit, Output} from '@angular/core';
import {
  AccessParameters,
  AppObject,
  ComponentAccess,
  DEFAULT_ACTIVATION_METADATA,
  DEFAULT_VISIBILITY_METADATA,
  InstallProduct
} from '../../application/app.datamodel';
import { ComponentsWithConfigsList, DeployedActorObject } from '../../configuration/componentsWithConfigs.datamodel';
import {
  ConfigurationFn,
  ConfigurationObject,
  UNKNOWN_CONFIG_NAME
} from '../../configuration/store/configuration.datamodel';
import { LSSEntityObject } from '../../shared/list/lssEntity.datamodel';
import {
  AdvancedComponentModel,
  AppComponentsType,
  ComponentCSGMetaData
} from '../../publish/component/store/component.datamodel';
import { ActorType } from '../../shared/datamodel';
import { FilterNoAccessConfigPipe } from '../../shared/pipes/filter-no-access-config.pipe';
import { AppService } from '../../application/app.service';
import { OpenModalService } from '../../core/services/openModal.service';
import { EditConfigurationComponent } from '../../shared/list/details/editConfiguration/editConfiguration.component';
import { ConfirmationModalComponent } from '../../shared/list/details/confirmationModal.component';
import { DeploymentLevel, DeploymentLevelCategory, DeploymentSection } from '../store/deployment.datamodel';
import { DeploymentService } from '../store/deployment.service';
import {
  DeploymentConfirmationModalComponent
} from '../deployment-confirmation-modal/deployment-confirmation-modal.component';
import { Subscription } from 'rxjs';
import { FormControl } from '@angular/forms';
import {PermissionsType} from '../../user/permissions/permissions.datamodel';
import {ActivationParametersService} from '../../activation-parameters/activation-parameters.service';

@Component({
    selector: 'ama-ng-app-store-deployment',
    templateUrl: './deployment.component.html',
    styleUrls: ['./deployment.component.scss'],
    standalone: false
})
export class DeploymentComponent implements OnInit, OnDestroy {
  @Input() app!: AppObject;
  @Input() loading!: boolean;
  @Input() isGlobalApp!: boolean;
  @Input() isFarmMapApp!: boolean;
  @Input() isOfficeApp!: boolean;
  isDeployedToSeco = model<boolean>(false);
  @Input() infoMessage: any;
  @Input() officeIds!: any[];
  @Input() components!: AdvancedComponentModel[];
  @Input() cmpsWithConfigs!: ComponentsWithConfigsList;
  @Input() deployedActors!: DeployedActorObject[];
  @Input() redefinedConfigSuccess!: string;
  @Input() appComponentsType!: AppComponentsType;
  @Input() selectedConfigurations!: { [p: string]: string };
  @Input() entitySelected!: LSSEntityObject;
  @Input() selectedConfigurationsObject: ConfigurationObject[] = [];
  @Output() isPushExpandedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  DEPLOYMENT_TARGETS = DeploymentLevel;
  DEPLOYMENT_SECTION = DeploymentSection;
  deploymentCategory!: DeploymentLevelCategory;

  userOfficeFilter: FormControl;

  selectedOffice: string | undefined = undefined;
  isAnyDeploymentChange = false;
  isOguDeploymentEnable = false;
  anyOrgLevelAccess = false;

  protected readonly PermissionsType = PermissionsType;

  private readonly subscriptions: Subscription = new Subscription();
  private readonly filterNoAccess = new FilterNoAccessConfigPipe();

  constructor(
    public readonly deploymentService: DeploymentService,
    public readonly appService: AppService,
    public readonly openModalService: OpenModalService,
    public readonly parameterService: ActivationParametersService
  ) {
    this.userOfficeFilter = new FormControl('');
  }

  ngOnInit() {
    this.subscriptions.add(
      this.deploymentService.componentAccessWithCategory$.subscribe(payload => {
        if (payload.componentAccess.length > 0) {
          this.pushAccess(payload.componentAccess, payload.category?.massivePushChunkSize as number);
        }
      })
    );
    this.subscriptions.add(
      this.deploymentService.isOguDeploymentEnabled$.subscribe(isEnabled => {
        this.isOguDeploymentEnable = isEnabled;
      })
    );
    this.subscriptions.add(
      this.deploymentService.isAnyChange$.subscribe(isAnyDeploymentChange => {
        this.isAnyDeploymentChange = isAnyDeploymentChange;
      })
    );
    this.subscriptions.add(
      this.deploymentService.selectedDeploymentLevelCategory$.subscribe(deploymentLevel => {
        this.deploymentCategory = deploymentLevel as DeploymentLevelCategory;
      })
    );
    this.subscriptions.add(
      this.deploymentService.selectedOffice$.subscribe(selectedOffice => {
        this.selectedOffice = selectedOffice!;
        if (this.isAnyDeploymentChange) {
          this.generateModalConfirmationForOfficeSearchChanges();
        } else {
          this.retrieveSelectedOfficeUsers(selectedOffice!);
        }
      })
    );
    this.subscriptions.add(
      this.deploymentService.isUnsavedChangesModalTriggered$.subscribe(trigger => {
        if (trigger) {
          const MODAL = this.openModalService.openDialog(DeploymentConfirmationModalComponent,
            {
              modal: {size: 'lg'}
            });
          MODAL.result.then(action => {
            if (action === 'CONFIRM') {
              this.pushApp();
            } else if (action === 'CANCEL') {
              this.cancel();
              this.deploymentService.dispatchUnsavedChangesModalClosed();
            }
          });
        }
      })
    );
    this.subscriptions.add(
        this.deploymentService.orgAndOguEntities$.subscribe(entities => {
          this.anyOrgLevelAccess = entities.some(
              entity => entity.actor.actorType === ActorType.ORGANISATION && entity.isDeployed);
        })
    );
    this.deploymentService.dispatchForceOnlyDeployedOfficesFilter(false);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  /**
   * Opens a dialog for configuring scripts
   */
  openConfigurationDialog($event: any): void {
    const selectedConfigurations = this.cmpsWithConfigs.components.reduce((selectedConfig, component) => {
      const defaultConfig = component.configurations
        .find(conf => ConfigurationFn.isDefaultConfig(conf));
      return {...selectedConfig, [defaultConfig?.componentId?.guid as string]: defaultConfig?.guid};
    }, {});
    const MODAL = this.openModalService.openDialog(EditConfigurationComponent, {
      modal: {size: "xl"},
      data:
        {
          cmpsWithConfigs: this.filterNoAccess.transform(this.cmpsWithConfigs),
          entitySelected: this.entitySelected,
          selectedConfigurations: selectedConfigurations,
          selectedEntities: $event,
          redefinedConfigSuccess: this.redefinedConfigSuccess,
          deployedActors: this.deployedActors,
          editConfiguration: true,
          deploymentCategory: this.deploymentCategory,
          appId: this.app.id,
          orgId: this.entitySelected.label,
          displayDynamicForm: this.appComponentsType === AppComponentsType.FUNCTIONAL_PARAM
        }
    });

    MODAL.result.then(action => {
      if (action === 'CONFIRM') {
        this.deploymentService.dispatchUnselectEntitiesToBeUndeployed(this.app.id!, $event);
      }
    });
  }

  /**
   * Moves entities selected to deploy to the deployment section
   */
  moveEntitiesToDeploy() {
    const configuration = {
      configurations: this.selectedConfigurationsObject.map(conf => conf.name) as string[],
      configurationsTooltip: this.selectedConfigurationsObject.map(conf => this.getConfigurationDisplayName(conf))
    };

    this.deploymentService.dispatchMoveSelectedEntitiesToDeploymentSection(this.app.id!, {...configuration});
    if (this.deploymentCategory.level === DeploymentLevel.USER) {
      this.infoMessage = {
        appId: this.app.id,
        message: "The changes will apply to all the user's offices in the organization",
        code: "info"
      };
    } else {
      this.infoMessage = {};
    }
  }

  /**
   * Moves entities selected to un-deploy to the un-deployment section
   */
  toUndeployEntities() {
    this.deploymentService.dispatchMoveSelectedEntitiesToUndeploymentSection(this.app.id!);

    if (this.deploymentCategory.level === DeploymentLevel.USER) {
      this.infoMessage = {
        appId: this.app.id,
        message: "The changes will apply to all the user's offices in the organization",
        code: "info"
      };
    } else {
      this.infoMessage = {};
    }
  }

  /**
   * Ask for user confirmation before deployment/undeployement
   *
   */
  askForConfirmation(event: any) {
    const MODAL = this.openModalService.openDialog(ConfirmationModalComponent,
      {
        modal: {size: 'xl'},
        data: {shouldDeploy: event.currentTarget.checked}
      });
    MODAL.result.then(action => this.globalPushConfirmed(action), () => this.globalPushCancelled());
  }

  /**
   * Remove UI changes if user doesn't confirm the global push
   *
   */
  globalPushCancelled() {
    this.isDeployedToSeco.update(value => !value);
  }

  /**
   * Global push confirmation callback
   * @param action
   */
  globalPushConfirmed(action: any) {
    if (action === 'CONFIRM') {
      this.appService.pushAccesses(this.app, [
        {
          actor: {
            actorName: InstallProduct.SELL_CONNECT.toString(),
            actorType: ActorType.PRODUCT,
          },
          parentActor: undefined,
          isAccessible: this.isDeployedToSeco(),
          isActive: true,
          isVisible: true,
          accessParameters: JSON.stringify(this.createAccessParameters())
        }
      ], this.selectedConfigurationsObject);
    } else if (action === 'CANCEL') {
      this.globalPushCancelled();
    }
  }

  /**
   * Publish accesses changes
   * @param accesses Component accesses list
   * @param massivePushChunkSize Chunk size for massive push
   */
  pushAccess(accesses: ComponentAccess[], massivePushChunkSize: number): void {
    if (accesses.length > 0) {
      this.appService.pushAccesses(this.app, accesses, this.selectedConfigurationsObject, massivePushChunkSize);
    } else {
      this.appService.restorePushAction(this.app.id!);
      this.infoMessage = {
        appId: this.app.id,
        message: "The app is already up-to-date",
        code: "success"
      };
    }
  }

  /**
   * Push app
   */
  pushApp(): void {
    const accessParameters: AccessParameters = this.createAccessParameters();
    this.deploymentService.dispatchCreateComponentAccesses(this.app.id!, JSON.stringify(accessParameters),
        this.entitySelected);
  }

  /**
   * Run the search in the two columns: available elements and deployed elements
   * @param value
   */
  applyFilterLists(value: string) {
    this.deploymentService.dispatchChangeFilterValue(value);
  }

  /**
   * Search for users in selected office
   */
  searchUsersOnSelectedOffice() {
    this.appService.restorePushAction(this.app.id!);
    this.deploymentService.dispatchChangeSelectedOffice(this.selectedOffice!);
  }

  /**
   * Removes all uncommitted changes
   */
  cancel() {
    this.appService.restorePushAction(this.app.id!);
    this.deploymentService.dispatchRemoveUncommittedChanges(this.app.id!);
  }

  /**
   * Changes the selected deployment level category
   *
   * @param event
   * @param deploymentLevel
   */
  changeDeploymentLevel(event: any, deploymentLevel: DeploymentLevel) {
    this.applyFilterLists('');
    this.deploymentService.dispatchForceOnlyDeployedOfficesFilter(false);
    this.userOfficeFilter.reset();

    event.target.checked = false;
    if (this.isAnyDeploymentChange) {
      const MODAL = this.openModalService.openDialog(DeploymentConfirmationModalComponent,
        {
          modal: {size: 'lg'}
        });
      MODAL.result.then(action => {
        if (action === 'CONFIRM') {
          this.appService.selectionChangedPushAction(this.app.id!, {});
          this.pushApp();
          this.deploymentService.dispatchChangeSelectedDeploymentLevel(this.app.id!, deploymentLevel);
          event.target.checked = true;
        } else if (action === 'CANCEL') {
          this.appService.selectionChangedPushAction(this.app.id!, {});
          this.cancel();
          this.deploymentService.dispatchChangeSelectedDeploymentLevel(this.app.id!, deploymentLevel);
          event.target.checked = true;
        }
      });
    } else {
      this.appService.selectionChangedPushAction(this.app.id!, {});
      this.deploymentService.dispatchChangeSelectedDeploymentLevel(this.app.id!, deploymentLevel);
      event.target.checked = true;
    }
  }

  /**
   * Apply the filter to show only deployed offices
   */
  applyDeployedOfficeFilter() {
    this.deploymentService.dispatchToggleOnlyDeployedOfficesFilter();
  }

  /**
   * Offices search uncommitted changes modal confirmation
   * @private
   */
  private generateModalConfirmationForOfficeSearchChanges() {
    const MODAL = this.openModalService.openDialog(DeploymentConfirmationModalComponent,
      {
        modal: {size: 'lg'}
      });
    MODAL.result.then(action => {
      if (action === 'CONFIRM') {
        this.pushApp();
        this.retrieveSelectedOfficeUsers(this.selectedOffice!);
      } else if (action === 'CANCEL') {
        this.cancel();
        this.retrieveSelectedOfficeUsers(this.selectedOffice!);
      } else {
        this.deploymentService.dispatchChangeSelectedOffice('');
      }
    });
  }

  /**
   * Retrieves users from selected office
   *
   * @param office Selected office name
   * @private
   */
  private retrieveSelectedOfficeUsers(office: string) {
    if (!office || office === "") {
      this.deploymentService.dispatchPopulateUserDeploymentsFromSelectedOffice(this.app.id!, null);
    } else {
      this.deploymentService.retrieveAndPopulateUsersFromSelectedOffice(
          this.app.id!, this.entitySelected, office);
    }
  }

  /**
   * Create component install request for a single component given its ID, metadata
   */
  private analyzeComponentMetadata(
    componentId: string,
    metadatas: ComponentCSGMetaData[],
    accessParameters: AccessParameters
  ): void {
    let isActive = true;
    let isVisible = true;
    let addComponentRequest = false;
    metadatas.forEach(metadata => {
      if (metadata.key === DEFAULT_ACTIVATION_METADATA) {
        isActive = this.isMetadataTrue(metadata.value);
        addComponentRequest = true;
      }
      if (metadata.key === DEFAULT_VISIBILITY_METADATA) {
        isVisible = this.isMetadataTrue(metadata.value);
        addComponentRequest = true;
      }
    });
    if (addComponentRequest) {
      accessParameters.visibility = Object.assign({},
        accessParameters.visibility,
        {[componentId]: isVisible});
      accessParameters.activation = Object.assign({},
        accessParameters.activation,
        {[componentId]: isActive});
    }
  }

  /**
   * Check if metadata value is true
   * @param metadata value of central storage metadata
   */
  private isMetadataTrue(metadata: string): boolean {
    return metadata.toLowerCase() === 'true';
  }

  /**
   * Create accessParameters object with selected configurations for each component and activation/visibility data
   * @private
   */
  private createAccessParameters(): AccessParameters {
    const accessParameters: AccessParameters = {};
    if (this.selectedConfigurations && Object.keys(this.selectedConfigurations).length > 0) {
      const configurations: Record<string, string> = this.createConfigurationsAccessParameters();
      if (Object.keys(configurations).length > 0) {
        accessParameters.configurations = configurations;
      }
    }
    if (this.components) {
      this.components.forEach(component => {
        if (component.metadata) {
          this.analyzeComponentMetadata(component.id, component.metadata, accessParameters);
        }
      });
    }
    return accessParameters;
  }

  /**
   * Create configurations map for access parameters
   * @private
   */
  private createConfigurationsAccessParameters(): Record<string, string> {
    const configurations: Record<string, string> = {};
    Object.entries(this.selectedConfigurations as Record<string, string>).forEach(([componentId, configurationId]) => {
      if (this.components.find(component => component.id === componentId)) {
        configurations[componentId] = configurationId;
      }
    });
    return configurations;
  }

  /**
   * Get display name of the configuration
   *
   * @param conf
   * @private
   */
  private getConfigurationDisplayName(conf: ConfigurationObject): string {
    if (!conf || conf?.name === UNKNOWN_CONFIG_NAME) {
      return UNKNOWN_CONFIG_NAME;
    } else if (conf.parentGuid) {
      return `${conf.name} - ${conf.actor?.actorName}`;
    } else {
      return conf.name!;
    }
  }
}
